import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Star, GitFork, Github, ExternalLink, Users, BookMarked } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GithubFeed = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/github`)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.645, 0.045, 0.355, 1] },
    },
  };

  const repos = data?.repositories?.slice(0, 6) || [];

  return (
    <section className="github-section" id="github">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
          className="section-header"
        >
          <h2 className="section-title">Live from GitHub</h2>
          <p className="section-subtitle">
            Real repositories pulled directly from my GitHub, kept fresh automatically.
          </p>
        </motion.div>

        {/* Profile Stats */}
        {data?.profile && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="github-stats"
          >
            <a
              href={data.profile.html_url}
              target="_blank"
              rel="noopener noreferrer"
              className="github-profile-link"
              data-testid="github-profile-link"
            >
              <Github size={18} />@{data.profile.login}
            </a>
            <div className="github-stat">
              <BookMarked size={16} />
              <span>{data.profile.public_repos} repositories</span>
            </div>
            <div className="github-stat">
              <Users size={16} />
              <span>{data.profile.followers} followers</span>
            </div>
          </motion.div>
        )}

        {loading && (
          <div className="github-grid">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="repo-card skeleton" data-testid="repo-skeleton" />
            ))}
          </div>
        )}

        {error && (
          <div className="github-error" data-testid="github-error">
            <p>Unable to load GitHub data right now. Visit my profile directly:</p>
            <a
              href="https://github.com/secopsarch"
              target="_blank"
              rel="noopener noreferrer"
              className="github-error-link"
            >
              <Github size={18} /> github.com/secopsarch
            </a>
          </div>
        )}

        {!loading && !error && (
          <motion.div
            ref={ref}
            variants={containerVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            className="github-grid"
          >
            {repos.map((repo) => (
              <motion.a
                key={repo.url}
                href={repo.url}
                target="_blank"
                rel="noopener noreferrer"
                variants={cardVariants}
                className="repo-card"
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                data-testid="repo-card"
              >
                <div className="repo-card-header">
                  <Github size={18} className="repo-icon" />
                  <ExternalLink size={15} className="repo-external" />
                </div>
                <h3 className="repo-name">{repo.name}</h3>
                <p className="repo-description">
                  {repo.description || 'No description provided.'}
                </p>
                <div className="repo-footer">
                  {repo.language && (
                    <span className="repo-language">
                      <span className="lang-dot" />
                      {repo.language}
                    </span>
                  )}
                  <span className="repo-meta">
                    <Star size={14} /> {repo.stars}
                  </span>
                  <span className="repo-meta">
                    <GitFork size={14} /> {repo.forks}
                  </span>
                </div>
              </motion.a>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default GithubFeed;
